import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchAgent247Config,
  updateAgent247NotificationRecipients,
} from "../../utils/api";

const SIMPLE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const E164_PHONE_RE = /^\+[1-9]\d{1,14}$/;

const normalizeEmails = (raw) =>
  Array.isArray(raw)
    ? Array.from(
        new Set(
          raw
            .map((entry) => String(entry || "").trim().toLowerCase())
            .filter(Boolean),
        ),
      )
    : [];

const normalizePhoneInput = (value = "") =>
  String(value ?? "")
    .replace(/\s+/g, "")
    .trim();

const normalizePhoneToE164 = (value) => {
  const trimmed = normalizePhoneInput(value);
  if (!trimmed) return "";
  if (trimmed.startsWith("+")) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return "";
};

const normalizePhones = (raw) =>
  Array.isArray(raw)
    ? Array.from(
        new Set(
          raw
            .map((entry) => normalizePhoneToE164(entry))
            .filter((phone) => phone && E164_PHONE_RE.test(phone)),
        ),
      )
    : [];

const isValidEmail = (value) => SIMPLE_EMAIL_REGEX.test(String(value || "").trim().toLowerCase());
const isValidPhone = (value) => E164_PHONE_RE.test(normalizePhoneToE164(value));

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: 999,
  background: "#dbeafe",
  color: "#0f172a",
  fontSize: 12,
  lineHeight: 1.2,
};

const chipRemoveStyle = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: 14,
  lineHeight: 1,
  padding: 0,
  color: "#334155",
};

const LOCATION_REQUIRED_MESSAGE =
  "Please select a location from the top menu first.";
const CONFIG_LOAD_FAILURE_NOTE =
  " If this problem continues, contact your administrator.";

const Agent247NotificationRecipients = ({ organizationId }) => {
  const [loading, setLoading] = useState(() => Boolean(organizationId));
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const isEditingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [notificationPhones, setNotificationPhones] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadRecipients = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      setHasLoadedOnce(false);
      setError(LOCATION_REQUIRED_MESSAGE);
      return;
    }
    if (isEditingRef.current) return;
    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }
    setError("");
    try {
      const config = await fetchAgent247Config(organizationId);
      if (isEditingRef.current) return;
      setNotificationEmails(normalizeEmails(config?.notificationEmails));
      setNotificationPhones(normalizePhones(config?.notificationPhones));
      hasLoadedOnceRef.current = true;
      setHasLoadedOnce(true);
    } catch (err) {
      const baseMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to load alert recipients.";
      setError(`${baseMessage}${CONFIG_LOAD_FAILURE_NOTE}`);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    hasLoadedOnceRef.current = false;
    setHasLoadedOnce(false);
  }, [organizationId]);

  useEffect(() => {
    void loadRecipients();
  }, [loadRecipients]);

  const markEditing = () => {
    isEditingRef.current = true;
  };

  const markNotEditing = () => {
    window.setTimeout(() => {
      isEditingRef.current = false;
    }, 150);
  };

  const addEmail = (rawEmail) => {
    const email = String(rawEmail || "").trim().toLowerCase();
    if (!email) return;
    if (!isValidEmail(email)) {
      setEmailError(`Invalid email: ${email}`);
      return;
    }
    setNotificationEmails((prev) => {
      const current = normalizeEmails(prev);
      if (current.includes(email)) return current;
      return [...current, email];
    });
    setEmailInput("");
    setEmailError("");
  };

  const removeEmail = (emailToRemove) => {
    setNotificationEmails((prev) =>
      normalizeEmails(prev).filter((entry) => entry !== emailToRemove),
    );
    setEmailError("");
  };

  const addPhone = (rawPhone) => {
    const phone = normalizePhoneToE164(rawPhone);
    if (!phone) return;
    if (!isValidPhone(rawPhone)) {
      setPhoneError(`Invalid phone: ${rawPhone}. Use E.164 format (e.g. +16095551234).`);
      return;
    }
    setNotificationPhones((prev) => {
      const current = normalizePhones(prev);
      if (current.includes(phone)) return current;
      return [...current, phone];
    });
    setPhoneInput("");
    setPhoneError("");
  };

  const removePhone = (phoneToRemove) => {
    setNotificationPhones((prev) =>
      normalizePhones(prev).filter((entry) => entry !== phoneToRemove),
    );
    setPhoneError("");
  };

  const buildEmailsForSave = () => {
    const emails = normalizeEmails(notificationEmails);
    const pending = String(emailInput || "").replace(/,+$/, "").trim().toLowerCase();
    if (pending && isValidEmail(pending) && !emails.includes(pending)) {
      emails.push(pending);
    }
    return emails;
  };

  const buildPhonesForSave = () => {
    const phones = normalizePhones(notificationPhones);
    const pending = normalizePhoneToE164(phoneInput);
    if (pending && isValidPhone(pending) && !phones.includes(pending)) {
      phones.push(pending);
    }
    return phones;
  };

  const handleSave = async () => {
    if (!organizationId) {
      setError(LOCATION_REQUIRED_MESSAGE);
      return;
    }
    setSaving(true);
    setMessage("");
    setError("");

    const emailsToSave = buildEmailsForSave();
    const phonesToSave = buildPhonesForSave();

    const invalidEmails = emailsToSave.filter((email) => !isValidEmail(email));
    if (invalidEmails.length) {
      setEmailError(`Invalid email: ${invalidEmails[0]}`);
      setSaving(false);
      return;
    }

    const invalidPhones = phonesToSave.filter((phone) => !isValidPhone(phone));
    if (invalidPhones.length) {
      setPhoneError(`Invalid phone: ${invalidPhones[0]}`);
      setSaving(false);
      return;
    }

    try {
      const updated = await updateAgent247NotificationRecipients(organizationId, {
        notificationEmails: emailsToSave,
        notificationPhones: phonesToSave,
      });
      setNotificationEmails(normalizeEmails(updated?.notificationEmails));
      setNotificationPhones(normalizePhones(updated?.notificationPhones));
      setEmailInput("");
      setPhoneInput("");
      setMessage("Alert recipients saved.");
    } catch (err) {
      const baseMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save alert recipients.";
      setError(`${baseMessage}${CONFIG_LOAD_FAILURE_NOTE}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ai-card" style={{ marginBottom: 16 }}>
      <div className="ai-card-header">
        <div>
          <p className="ai-card-note">Alerts</p>
          <h3 className="ai-card-title">Inquiry Alert Recipients</h3>
        </div>
        <button
          type="button"
          className="ai-button ai-button-primary ai-button-sm"
          onClick={handleSave}
          disabled={saving || (loading && !hasLoadedOnce) || !organizationId}
        >
          {saving ? "Saving..." : "Save Recipients"}
        </button>
      </div>
      <div>
        {!organizationId && !loading ? (
          <div className="ai-alert ai-alert-error">{LOCATION_REQUIRED_MESSAGE}</div>
        ) : null}
        {loading && !hasLoadedOnce ? (
          <p className="ai-card-note">Loading alert recipients...</p>
        ) : (
          <>
            {message ? <div className="ai-alert ai-alert-success">{message}</div> : null}
            {error && organizationId ? (
              <div className="ai-alert ai-alert-error">{error}</div>
            ) : null}
            {!organizationId ? null : (
            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <label className="ai-card-note" style={{ display: "block", marginBottom: 8 }}>
                  Notification Emails
                </label>
                <div
                  className="ai-input"
                  style={{
                    minHeight: 44,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                    padding: 6,
                  }}
                >
                  {notificationEmails.map((email) => (
                    <span key={email} style={chipStyle}>
                      {email}
                      <button
                        type="button"
                        style={chipRemoveStyle}
                        onClick={() => removeEmail(email)}
                        aria-label={`Remove ${email}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="email"
                    value={emailInput}
                    onFocus={markEditing}
                    onBlur={markNotEditing}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                      if (emailError) setEmailError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addEmail(emailInput);
                      }
                    }}
                    placeholder="Add email and press Enter"
                    style={{
                      border: "none",
                      outline: "none",
                      flex: 1,
                      minWidth: 220,
                      background: "transparent",
                      fontSize: 14,
                      padding: "4px 2px",
                    }}
                  />
                </div>
                {emailError ? (
                  <small style={{ color: "#b91c1c", display: "block", marginTop: 6 }}>
                    {emailError}
                  </small>
                ) : null}
                <small className="ai-card-note" style={{ display: "block", marginTop: 6 }}>
                  Email summaries are sent after each completed 24/7 inquiry call.
                </small>
              </div>

              <div>
                <label className="ai-card-note" style={{ display: "block", marginBottom: 8 }}>
                  Notification Phones (SMS)
                </label>
                <div
                  className="ai-input"
                  style={{
                    minHeight: 44,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                    padding: 6,
                  }}
                >
                  {notificationPhones.map((phone) => (
                    <span key={phone} style={chipStyle}>
                      {phone}
                      <button
                        type="button"
                        style={chipRemoveStyle}
                        onClick={() => removePhone(phone)}
                        aria-label={`Remove ${phone}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={phoneInput}
                    onFocus={markEditing}
                    onBlur={markNotEditing}
                    onChange={(e) => {
                      setPhoneInput(e.target.value);
                      if (phoneError) setPhoneError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addPhone(phoneInput);
                      }
                    }}
                    placeholder="Add phone (+16095551234) and press Enter"
                    style={{
                      border: "none",
                      outline: "none",
                      flex: 1,
                      minWidth: 220,
                      background: "transparent",
                      fontSize: 14,
                      padding: "4px 2px",
                    }}
                  />
                </div>
                {phoneError ? (
                  <small style={{ color: "#b91c1c", display: "block", marginTop: 6 }}>
                    {phoneError}
                  </small>
                ) : null}
                <small className="ai-card-note" style={{ display: "block", marginTop: 6 }}>
                  Text alerts include a portal login link for each new inquiry.
                </small>
              </div>
            </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Agent247NotificationRecipients;
